// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — the 5-state connection state machine (ABB-S7b)
// ═══════════════════════════════════════════════════════════════════════════
//
// The zustand slice that drives the S7c Connect indicator + owns the connect /
// disconnect lifecycle of the agent-bridge `initAgentBridge` handle. It is SEPARATE
// from `editorStore` ON PURPOSE: a disconnection (or any connection-state change)
// MUST NOT touch the drawing's undo/redo history — the bridge connection is a
// session-level concern (like `sysvarStore`), not a transactional document mutation.
//
// THE 5 STATES (the coarse machine S7c renders; DISTINCT from the client's 8-value
// internal `AgentBridgeStatus`, which this maps DOWN to these five):
//   • disconnected — the default; no socket, no handle (or a torn-down one).
//   • connecting   — `connect()` called; the socket is opening / handshaking
//                    (mapped from the client `connecting` / `reconnecting`).
//   • connected    — the socket is open + welcomed, but THIS tab is an OBSERVER
//                    (mapped from `connected-observer`): paired-but-not-the-active-seat.
//   • paired       — the socket is open + welcomed + THIS tab holds the ACTIVE seat
//                    (mapped from `connected-active`): the agent loop can run.
//   • error        — a terminal connection failure (mapped from `companion-not-
//                    reachable` / `token-rejected` / `protocol-version-mismatch`).
//
// DEFAULT-OFF (INV-OFF, ПРИОРИТЕТ #1): the store starts `disconnected` with NO
// handle. `connect()` is the ONLY thing that builds the handle + opens a socket;
// merely importing this module (or reading the store) mounts NOTHING. `initAgentBridge`
// is injected via `connect`'s factory param (default the real one) so a test drives a
// MOCK socket with no real network.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports zustand + the store-coupled `agentBridgeMount`
// (the handle factory) + the client status TYPE. It does NOT import editorStore (the
// separation is the whole point). The mount module is the store-coupled sink; this
// store is a thin lifecycle driver on top of it. `npx depcruise` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import { create } from "zustand";

import type { AgentBridgeStatus } from "../agentBridge";
import {
  type AgentBridgeHandle,
  initAgentBridge,
  type InitAgentBridgeOptions,
} from "./agentBridgeMount";

/** The 5 coarse connection states the S7c indicator renders. */
export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "paired"
  | "error";

/**
 * The LOCALIZATION KEY SUFFIX for an `error`-state detail. The store carries this
 * stable key (never English prose) so the indicator can localize it at the SOURCE
 * via `t(\`agent_connect.error.${key}\`)` — RU users see RU, EN users see EN. Each
 * suffix has a matching `agent_connect.error.<suffix>` key in BOTH locales of
 * `messages.ts`; the typed union makes the template literal a valid `MessageKey`.
 *   • companion_unreachable — the loopback companion did not answer.
 *   • token_rejected        — the pairing token was rejected by the companion.
 *   • protocol_mismatch     — the companion protocol version != this build.
 *   • connect_failed        — any other synchronous build/connect failure (the
 *                             raw technical detail is `console.warn`'d for devs).
 */
export type ConnectionErrorKey =
  | "companion_unreachable"
  | "token_rejected"
  | "protocol_mismatch"
  | "connect_failed";

/**
 * The DI factory `connect` uses to build the agent-bridge handle. Defaults to the
 * real `initAgentBridge`; a test injects one that wires a mock socket so no real
 * network opens. The factory receives the `onStatusChange` bridge so the client's
 * status edges drive `setStatus`.
 */
export type AgentBridgeHandleFactory = (opts: InitAgentBridgeOptions) => AgentBridgeHandle;

/**
 * Map the client's 8-value internal `AgentBridgeStatus` DOWN to the coarse 5-state
 * machine the indicator renders. The client owns the fine-grained transitions
 * (handshake / reconnect / token-reject / mismatch); this projects them onto the
 * five high-level states.
 */
export function projectClientStatus(status: AgentBridgeStatus): ConnectionStatus {
  switch (status) {
    case "disconnected":
      return "disconnected";
    case "connecting":
    case "reconnecting":
      return "connecting";
    case "connected-observer":
      return "connected"; // paired-but-observer: connected, not the active seat.
    case "connected-active":
      return "paired"; // the active seat — the agent loop can run.
    case "companion-not-reachable":
    case "token-rejected":
    case "protocol-version-mismatch":
      return "error";
    default: {
      // Exhaustiveness guard: a NEW AgentBridgeStatus member must be mapped here
      // explicitly (never silently bucketed) — the never-assignment is a tsc error.
      const _exhaustive: never = status;
      void _exhaustive;
      return "error";
    }
  }
}

/** The connection-store shape (state + the lifecycle actions S7c calls). */
export interface ConnectionStoreState {
  /** The current coarse connection state (the indicator reads this). */
  status: ConnectionStatus;
  /** The loopback companion URL the indicator last connected with (null when never connected). */
  companionUrl: string | null;
  /** The pairing token the indicator last connected with (null when disconnected). */
  pairingToken: string | null;
  /**
   * The localization KEY SUFFIX for the `error`-state detail (null otherwise).
   * NOT English prose — the indicator localizes it via `t()` at render. See
   * `ConnectionErrorKey`.
   */
  error: ConnectionErrorKey | null;
  /**
   * The live agent-bridge handle (null until `connect`). NOT rendered — the indicator
   * reads `status`; the handle is held so `disconnect` can tear it down. Exposed for
   * S7c / tests to reach `handle.client` for the role/epoch readout.
   */
  handle: AgentBridgeHandle | null;
  /**
   * [@codex B] The token the CURRENT `handle` last actually `connect()`ed with (null when
   * no handle). The reuse predicate keys on this: a repeat connect to the SAME url is only
   * idempotent-reusable when the TOKEN is ALSO unchanged. A token change to the same url
   * takes the rebuild branch (disconnect → fresh factory) so the new token actually
   * re-handshakes — the client captures the token in its hello on `connect`, but a no-op
   * double-open of the SAME live socket would never re-send the hello, so a same-url
   * token-only change would be silently stranded if we reused the live handle. DISTINCT
   * from `pairingToken` (the last REQUESTED token, set on every connect incl. idempotent).
   */
  handleConnectedToken: string | null;
  /**
   * Open the loopback WS + pair. Builds the handle with the REQUESTED url (default-OFF
   * until here), wires the client's status edges into `setStatus`, and connects. The
   * client captures `wsUrl` at CONSTRUCTION (it cannot be re-pointed by `connect`), so:
   *   • a connect to a DIFFERENT url than the existing handle's REBUILDS it (the old
   *     socket is torn down first) — the reported `companionUrl` always equals the
   *     socket's actual target (@codex P2 finding 1);
   *   • a repeat connect to the SAME url AND the SAME token REUSES the handle and is
   *     IDEMPOTENT — when the handle is already `connected` / `paired` it does NOT
   *     optimistically downgrade to `connecting` (the client no-ops a double-open with no
   *     follow-up status edge, so a `connecting` would wedge the store — @codex P2 finding 3);
   *   • a connect to the SAME url with a CHANGED token REBUILDS too (@codex B) — the client
   *     captures the token in its hello at `connect`, so reusing the live socket would never
   *     re-handshake with the new token (silently stranded); rebuilding re-opens + re-hellos.
   *
   * [@codex A] The WHOLE build+connect span (factory construction through `handle.connect`)
   * is wrapped in ONE try-catch: a synchronous throw — the client's loopback-guard refusing a
   * non-loopback url, the mount handle's url-mismatch guard, or any factory/construction
   * failure — tears down a partially-built handle and sets `error` rather than leaving the
   * store WEDGED at `connecting`.
   *
   * `factory` is DI (default the real `initAgentBridge`) so a test drives a mock socket.
   */
  connect: (url: string, token: string, factory?: AgentBridgeHandleFactory) => void;
  /** Tear down the socket + the handle → `disconnected`. Never touches editorStore. */
  disconnect: () => void;
  /**
   * Directly set the coarse status (the client's `onStatusChange` bridge calls this;
   * also the test seam). Carries an optional error KEY SUFFIX for the `error` state.
   */
  setStatus: (status: ConnectionStatus, error?: ConnectionErrorKey | null) => void;
}

/**
 * The localization KEY SUFFIX for each terminal client error status (null for a
 * non-error status). The indicator localizes it via `t(\`agent_connect.error.
 * ${suffix}\`)`; the store carries the stable KEY, never English prose, so RU
 * users see RU. Each suffix has a matching `agent_connect.error.<suffix>` key in
 * both locales of `messages.ts`.
 */
function errorDetailFor(status: AgentBridgeStatus): ConnectionErrorKey | null {
  switch (status) {
    case "companion-not-reachable":
      return "companion_unreachable";
    case "token-rejected":
      return "token_rejected";
    case "protocol-version-mismatch":
      return "protocol_mismatch";
    default:
      return null;
  }
}

/**
 * The connection store. SEPARATE from editorStore (a disconnect must not touch
 * drawing undo/redo). Default `status='disconnected'`, no handle (INV-OFF). The
 * handle (and thus the socket) is built ONLY on `connect`.
 */
export const useConnectionStore = create<ConnectionStoreState>((set, get) => ({
  status: "disconnected",
  companionUrl: null,
  pairingToken: null,
  error: null,
  handle: null,
  handleConnectedToken: null,

  connect: (url, token, factory = initAgentBridge) => {
    const existing = get().handle;
    const currentStatus = get().status;

    // [@codex P2 — finding 1] The client captures `wsUrl` at CONSTRUCTION; `handle.connect`
    // CANNOT re-point the socket. So a handle is only re-usable for the SAME url.
    // [@codex B] AND the SAME token: the client captures the token in its hello at `connect`,
    // and a repeat connect to the SAME live socket is a client no-op (it never re-hellos), so
    // a same-url token-only CHANGE reusing the live handle would silently strand the new token.
    // REUSE only when BOTH url AND token match the handle's last connect; otherwise DISCONNECT
    // the old handle + REBUILD so the new url/token actually re-handshakes.
    const reusable =
      existing !== null && existing.wsUrl === url && get().handleConnectedToken === token;

    // [@codex A] Wrap the WHOLE build+connect span in ONE try-catch. A synchronous throw
    // (the client's `assertLoopbackWsTarget` loopback-guard on a non-loopback url, the mount
    // handle's url-mismatch guard, or a factory/construction failure) must NOT leave the store
    // WEDGED at `connecting`. On any throw: tear down a handle if one was built, then set
    // `error` with the failure detail.
    let handle: AgentBridgeHandle | null = null;
    try {
      if (existing !== null && !reusable) {
        existing.disconnect(); // a different url/token → tear the old socket down before rebuilding.
      }
      handle = reusable
        ? existing
        : factory({
            // [@codex P2 — finding 1] Build the handle with the REQUESTED url so the socket
            // opens THIS target. With the default factory a non-loopback url is refused
            // fail-closed by the client's loopback guard → an `error` state, never a silent
            // wrong-url connect.
            wsUrl: url,
            // Bridge the client's fine-grained status edges DOWN to the coarse 5-state
            // machine. The client drives this on every transition (handshake / reconnect /
            // token-reject / mismatch); the store projects it + records an error detail.
            onStatusChange: (clientStatus) => {
              set({
                status: projectClientStatus(clientStatus),
                error: errorDetailFor(clientStatus),
              });
            },
          });

      // [@codex P2 — finding 3] Do NOT optimistically downgrade an ALREADY-connected handle to
      // `connecting`. A repeat connect() to the SAME url+token while the client already owns an
      // OPEN socket (a re-click after `paired` / `connected`) is a no-op in the client
      // (`AgentBridgeClient.connect` returns early when the socket is non-null) → NO follow-up
      // `onStatusChange` would re-advance the status, so an optimistic `connecting` would WEDGE
      // the store at connecting though the client is still connected. Preserve the current
      // projected status in that case (an idempotent reconnect). A FRESH handle (rebuilt /
      // first connect), or a same-url handle that is NOT yet connected, takes the optimistic
      // `connecting` edge as before (the client's onStatusChange confirms / advances it).
      const isIdempotentReconnect =
        reusable && (currentStatus === "connected" || currentStatus === "paired");
      set({
        handle,
        companionUrl: url,
        pairingToken: token,
        // [@codex B] Record the token this handle is being connected with so a later same-url
        // connect can detect a token change + rebuild.
        handleConnectedToken: token,
        ...(isIdempotentReconnect ? {} : { status: "connecting", error: null }),
      });
      handle.connect(url, token);
    } catch (err) {
      // [@codex A] The build / connect threw (loopback-guard / url-mismatch / construction).
      // Tear down a handle if one was built (it may hold a half-open socket) and surface the
      // failure as `error` — NEVER leave the store wedged at `connecting`. Drop the handle so a
      // later connect rebuilds fresh; null the connected-token so no stale reuse.
      try {
        handle?.disconnect();
      } catch {
        /* disconnect on a half-built handle is best-effort — swallow a teardown throw */
      }
      // Localize at the SOURCE: the store carries the stable KEY SUFFIX
      // `connect_failed`, NOT the raw English `err.message` (RU users must see RU).
      // The technical detail is not lost — `console.warn` surfaces it to devs in
      // the console, it just never reaches the user-facing status line raw.
      console.warn("agent-bridge connect failed", err);
      set({
        status: "error",
        error: "connect_failed",
        companionUrl: url,
        pairingToken: token,
        handle: null,
        handleConnectedToken: null,
      });
    }
  },

  disconnect: () => {
    const { handle } = get();
    handle?.disconnect();
    // Reset to the default state — but NEVER touch editorStore (this store is separate
    // from the drawing's transactional undo/redo BY DESIGN). The handle is dropped so a
    // later connect rebuilds it fresh.
    set({
      status: "disconnected",
      pairingToken: null,
      error: null,
      handle: null,
      handleConnectedToken: null,
    });
  },

  setStatus: (status, error = null) => {
    set({ status, error });
  },
}));

// ── ABB-S7c — Этап-2 scenario readback seam (read-only, NON-SENSITIVE projection) ──
// The visual-gate scenarios (docs/ui-reference/agent-bridge-connect/scenario.json)
// read the TYPED coarse status off the live store via this getter
// (`window.connectionGetState().status`) to assert state transitions without
// scraping DOM text. Guarded on `window` so it no-ops under SSR / node test
// runners; mirrors the `window.editorGetState` test seam in editorStore.ts.
//
// This installs in EVERY browser runtime — INCLUDING production builds, because
// the visual gate runs against a `vite build` → `vite preview` PRODUCTION bundle
// where `import.meta.env.DEV` is false (so gating on DEV would break the gate).
// It therefore returns ONLY a NON-SENSITIVE projection: the coarse `status` plus
// the (now i18n-key, non-secret) `error`. The `pairingToken` (a pairing SECRET)
// and the live `handle` are DELIBERATELY OMITTED so a same-page script can never
// read the pairing token via `window.connectionGetState()`.
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).connectionGetState = () => {
    const s = useConnectionStore.getState();
    // Non-sensitive only — NEVER expose pairingToken / handle on window.
    return { status: s.status, error: s.error };
  };
}
